import { useState } from "react";
import { ArrowRight } from "lucide-react";
import { normaliseRegistration } from "@autopic/shared";
import { Button } from "../../components/Button";
import { StepHeader } from "../../components/StepHeader";
import { TrustStrip } from "../../components/TrustStrip";
import { ServiceIcon } from "../../components/icons";
import { api, ApiError } from "../../api/client";
import { track } from "../../analytics/analytics";
import { useSession } from "../../store/sessionStore";
import type { StepProps } from "../stepTypes";
import flow from "../BookingFlow.module.css";
import s from "../steps.module.css";

/**
 * Render a headline with the landing-page idiom: the first sentence in the
 * display serif, the rest as italic gold emphasis. Falls back gracefully when
 * the headline has no sentence break.
 */
function renderHeadline(headline: string): React.ReactNode {
  const m = headline.match(/^(.*?[.!?])\s+(.+)$/);
  if (!m) return headline;
  return (
    <>
      {m[1]} <em>{m[2]}</em>
    </>
  );
}

/** Step 1 — UK plate entry + "I don't know my reg" → manual path. */
export function RegistrationStep({ onNext, goTo, saveLead }: StepProps) {
  const config = useSession((st) => st.config)!;
  const setVehicle = useSession((st) => st.setVehicle);
  const setVehicleRegistration = useSession((st) => st.setVehicleRegistration);
  const branchId = useSession((st) => st.branchId);
  const setAvailableServices = useSession((st) => st.setAvailableServices);
  const existingReg = useSession((st) => st.vehicleRegistration);

  const [reg, setReg] = useState(existingReg ?? "");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const clean = normaliseRegistration(reg);
  const canSubmit = clean.length >= 2 && clean.length <= 8;
  const selectedLocation = config.branches.find(
    (branch) => branch.id === branchId,
  );

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!canSubmit || loading) return;
    setLoading(true);
    setError(null);
    track("registration_entered", { registration: clean });
    setVehicleRegistration(clean);

    try {
      if (!config.sandbox) {
        // Fixed-location installs are already pinned by config, so initialise
        // Garage Hive here instead of showing a redundant location step.
        if (branchId) {
          const result = await api.getGarageHiveOptions(config.id, {
            branchId,
            vehicleRegistration: clean,
            serviceIds: [],
          });
          setVehicle(result.vehicle, { found: true });
          setVehicleRegistration(result.vehicle.registration);
          setAvailableServices(result.services);
          track("vehicle_found", {
            make: result.vehicle.make,
            model: result.vehicle.model,
          });
        } else {
          // Selectable multi-location installs initialise after branch choice.
          setVehicle(null, { found: false });
        }
        saveLead("registration");
        onNext();
        return;
      }
      const result = await api.lookupVehicle(clean, config.id);
      saveLead("registration");
      if (result.status === "found") {
        setVehicle(result.vehicle, { found: true });
        setVehicleRegistration(result.vehicle.registration);
        track("vehicle_found", {
          make: result.vehicle.make,
          model: result.vehicle.model,
        });
        onNext();
      } else {
        // Not found → clear any prior vehicle and force the manual-entry step
        // (goTo clears any "return to summary" edit flag so the user can fill it).
        setVehicle(null, { found: false });
        track("vehicle_not_found", { registration: clean });
        goTo("vehicle");
      }
    } catch (err) {
      setError(
        err instanceof ApiError
          ? err.message
          : "Something went wrong looking up your reg. Please try again.",
      );
    } finally {
      setLoading(false);
    }
  }

  return (
    <form onSubmit={handleSubmit} noValidate>
      <StepHeader
        kicker={selectedLocation?.name ?? "Choose your location"}
        title={renderHeadline(config.copy.headline)}
        subtitle={config.copy.subheadline}
      />

      <div className={s.plateInputWrap}>
        <div className={s.plateField}>
          <span className={s.plateBadge}>🇬🇧 Enter your registration</span>
          <input
            className={s.plateInput}
            value={reg}
            onChange={(e) => setReg(e.target.value.toUpperCase())}
            placeholder="AB12 CDE"
            aria-label={config.copy.registrationLabel}
            autoComplete="off"
            autoCapitalize="characters"
            spellCheck={false}
            maxLength={10}
            inputMode="text"
            enterKeyHint="search"
          />
        </div>
        {error && (
          <p
            role="alert"
            style={{ color: "var(--ap-danger)", fontSize: 13, fontWeight: 600 }}
          >
            {error}
          </p>
        )}
      </div>

      <div className={s.actions}>
        <Button
          type="submit"
          block
          loading={loading}
          disabled={!canSubmit}
          trailingIcon={<ArrowRight size={18} />}
        >
          {config.copy.ctaQuote}
        </Button>
        {config.sandbox && (
          <button
            type="button"
            className={flow.linkBtn}
            onClick={() => {
              setVehicle(null, { found: false });
              setVehicleRegistration(null);
              track("vehicle_not_found", { reason: "user_skipped" });
              goTo("vehicle");
            }}
          >
            {config.copy.registrationHelp}
          </button>
        )}
      </div>

      {config.trust.enabled && (
        <div className={flow.trustWrap} style={{ marginTop: 22 }}>
          <TrustStrip trust={config.trust} />
        </div>
      )}

      {/* Subtle reassurance line, echoing the research journeys. */}
      <p className={`${flow.footerNote}`} style={{ marginTop: 18 }}>
        <ServiceIcon icon="shield-check" size={13} /> We only use your details
        to prepare your quote.
      </p>
    </form>
  );
}
